if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.handleAjaxProductLinkClick = this.handleAjaxProductLinkClick.bind(this);
      this.captureOpenCollapsibleBlocks = this.captureOpenCollapsibleBlocks.bind(this);
      this.applyOpenCollapsibleBlocks = this.applyOpenCollapsibleBlocks.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.addEventListener("click", this.handleAjaxProductLinkClick);
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.removeEventListener("click", this.handleAjaxProductLinkClick);
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      this.initQuantityHandlers();

      if (this.currentVariant && !this.showFeaturedMedia) {
        this.updateMedia(this.currentVariant);
      }
    }

    getVariantGroupImagesData() {
      return (
        JSON.parse(
          this.querySelector('#variantGroup[type="application/json"]')
            .textContent
        ) || {}
      );
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode),
      });
    }

    handleAjaxProductLinkClick(event) {
      const link = event.target.closest("[data-ajax-product-link]");
      if (!link || !this.contains(link)) return;
      if (event.defaultPrevented) return;
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const productUrl = link.getAttribute("href");
      if (!productUrl || productUrl === this.dataset.url) return;

      event.preventDefault();

      this.renderProductInfo({
        requestUrl: `${productUrl}?section_id=${this.sectionId}`,
        callback: this.handlePartialProductLinkSwap(productUrl, this.viewMode),
      });
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => response.text())
        .then((responseText) => {
          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
        })
        .then(() => {
          // set focus to last clicked option value
          if (targetId) {
            document.querySelector(`#${targetId}`)?.focus();
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        this.syncProductBlocks(html, '.main-product__block-custom_text');

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        this.currentVariant = variant;
        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.MinimogStrings.soldOut
        );

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    handlePartialProductLinkSwap(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const productInfo = html.querySelector('product-info');
        if (!productInfo) return;

        const variant = this.getSelectedVariant(productInfo);
        const openCollapsibleBlocks = this.captureOpenCollapsibleBlocks();
        this.applyOpenCollapsibleBlocks(productInfo, openCollapsibleBlocks);

        this.updateURL(productUrl, variant?.id);
        this.syncProductInfoState(productInfo);
        this.syncCurrentVariantMarker(productInfo);
        this.syncFlavorTitle(productInfo);
        this.syncFlavorLinks(productInfo);
        this.syncVariantPicker(productInfo);
        this.syncProductBlock(productInfo, '.main-product__block-price');
        this.syncProductBlock(productInfo, '.main-product__block-buy_buttons');
        this.syncProductBlocks(productInfo, '.main-product__block-sealsubs_widget');
        this.syncProductBlocks(productInfo, '.main-product__block-volume_buy_block');
        this.syncProductBlocks(productInfo, '[class~="main-product__block-@app"]');
        this.syncProductBlock(productInfo, '.main-product__block-meta');
        this.syncProductBlocks(productInfo, '.main-product__block-collapsible_tab');
        this.syncProductBlocks(productInfo, '.main-product__block-custom_text');
        this.syncProductBlocks(productInfo, '.main-product__block-custom_liquid');
        this.syncProductMedia(productInfo);
        this.syncStickyAtc(productInfo);
        this.syncEmbeddedJson(productInfo, '#variantGroup[type="application/json"]');
        this.syncVariantFlavorData(html);
        this.syncExternalProductSections(productUrl);

        this.currentVariant = variant;
        this.refreshProductState();

        const activeVariantId = variant?.id || this.productForm?.variantIdInput?.value;
        if (activeVariantId) {
          this.updateVariantInputs(activeVariantId);
        }

        if (variant) {
          this.pickupAvailability?.update(variant);

          MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
            data: { variant, sectionId: this.sectionId, html },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );
        }
      };
    }

    syncProductInfoState(productInfo) {
      const datasetKeys = [
        'url',
        'productId',
        'productHandle',
        'enableVariantGroupImages',
        'disableSelectedVariantDefault',
        'showFeaturedMedia',
        'productFlavorName',
        'productFlavorColor',
      ];

      datasetKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(productInfo.dataset, key)) {
          this.dataset[key] = productInfo.dataset[key];
        }
      });

      if (productInfo.getAttribute('style') !== null) {
        this.setAttribute('style', productInfo.getAttribute('style'));
      }

      if (window.MinimogSettings) {
        window.MinimogSettings.productHandle = this.dataset.productHandle;
        window.MinimogSettings.productId = Number(this.dataset.productId);
      }

      const recentlyViewed = this.querySelector('product-recently-viewed');
      const nextRecentlyViewed = productInfo.querySelector('product-recently-viewed');
      if (recentlyViewed && nextRecentlyViewed) {
        recentlyViewed.dataset.productId = nextRecentlyViewed.dataset.productId;
      }
    }

    syncCurrentVariantMarker(productInfo) {
      const currentMarker = this.querySelector('input[type="hidden"][name="id"][data-selected-variant][disabled]');
      const nextMarker = productInfo.querySelector('input[type="hidden"][name="id"][data-selected-variant][disabled]');
      if (!currentMarker || !nextMarker) return;

      currentMarker.value = nextMarker.value;
      currentMarker.dataset.selectedVariant = nextMarker.dataset.selectedVariant || '';
    }

    syncFlavorTitle(productInfo) {
      const currentTitle = this.querySelector('.main-product__block-title .m-product-title');
      const nextTitle = productInfo.querySelector('.main-product__block-title .m-product-title');
      if (!currentTitle || !nextTitle) return;

      const currentFlavor = currentTitle.querySelector('.m-product-title__flavor');
      const nextFlavor = nextTitle.querySelector('.m-product-title__flavor');

      currentTitle.classList.toggle('m-product-title--split', nextTitle.classList.contains('m-product-title--split'));

      if (nextFlavor) {
        if (currentFlavor) {
          currentFlavor.textContent = nextFlavor.textContent;
        } else {
          currentTitle.appendChild(nextFlavor.cloneNode(true));
        }
      } else if (currentFlavor) {
        currentFlavor.remove();
      }
    }

    syncFlavorLinks(productInfo) {
      const currentBlock = this.querySelector('.main-product__block-flavor_product_links');
      const nextBlock = productInfo.querySelector('.main-product__block-flavor_product_links');
      if (!currentBlock || !nextBlock) return;

      const currentTiles = Array.from(currentBlock.querySelectorAll('.m-product-option--node'));
      const nextTiles = Array.from(nextBlock.querySelectorAll('.m-product-option--node'));

      const currentHrefs = currentTiles.map((tile) => tile.querySelector('[data-option-type="image"]')?.getAttribute('href') || '');
      const nextHrefs = nextTiles.map((tile) => tile.querySelector('[data-option-type="image"]')?.getAttribute('href') || '');

      if (currentTiles.length !== nextTiles.length || currentHrefs.join('|') !== nextHrefs.join('|')) {
        this.syncProductBlock(productInfo, '.main-product__block-flavor_product_links');
        return;
      }

      currentTiles.forEach((tile, index) => {
        const imageLink = tile.querySelector('.m-product-option--node__label');
        const textLink = tile.querySelector('.m-product-option--image-text');
        const nextImageLink = nextTiles[index].querySelector('.m-product-option--node__label');
        const nextTextLink = nextTiles[index].querySelector('.m-product-option--image-text');

        if (!imageLink || !textLink || !nextImageLink || !nextTextLink) return;

        imageLink.className = nextImageLink.className;
        imageLink.setAttribute('style', nextImageLink.getAttribute('style') || '');

        if (nextImageLink.hasAttribute('aria-current')) {
          imageLink.setAttribute('aria-current', nextImageLink.getAttribute('aria-current'));
          textLink.setAttribute('aria-current', nextTextLink.getAttribute('aria-current'));
        } else {
          imageLink.removeAttribute('aria-current');
          textLink.removeAttribute('aria-current');
        }
      });
    }

    syncVariantPicker(productInfo) {
      const nextVariantPicker = productInfo.querySelector('variant-picker');

      if (this.variantPicker && nextVariantPicker) {
        HTMLUpdateUtility.viewTransition(this.variantPicker, nextVariantPicker, this.preProcessHtmlCallbacks);
        return;
      }

      this.syncProductBlock(productInfo, '.main-product__block-variant_picker');
    }

    syncProductBlock(productInfo, selector) {
      const currentBlock = this.querySelector(selector);
      const nextBlock = productInfo.querySelector(selector);

      if (currentBlock && nextBlock) {
        HTMLUpdateUtility.viewTransition(
          currentBlock,
          nextBlock,
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );
      } else if (currentBlock && !nextBlock) {
        currentBlock.remove();
      }
    }

    syncProductBlocks(productInfo, selector) {
      const currentBlocks = Array.from(this.querySelectorAll(selector));
      const nextBlocks = Array.from(productInfo.querySelectorAll(selector));

      if (!currentBlocks.length && !nextBlocks.length) return;

      if (currentBlocks.length !== nextBlocks.length) {
        currentBlocks.forEach((currentBlock, index) => {
          const nextBlock = nextBlocks[index];

          if (currentBlock && nextBlock) {
            currentBlock.replaceWith(nextBlock.cloneNode(true));
          } else if (currentBlock && !nextBlock) {
            currentBlock.remove();
          }
        });

        if (nextBlocks.length > currentBlocks.length) {
          const parent =
            currentBlocks[0]?.parentNode ||
            this.querySelector('.m-main-product--info .m-product-info--wrapper') ||
            this;

          nextBlocks.slice(currentBlocks.length).forEach((nextBlock) => {
            parent.appendChild(nextBlock.cloneNode(true));
          });
        }

        return;
      }

      currentBlocks.forEach((currentBlock, index) => {
        const nextBlock = nextBlocks[index];

        if (!nextBlock) {
          currentBlock.remove();
          return;
        }

        HTMLUpdateUtility.viewTransition(
          currentBlock,
          nextBlock,
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );
      });
    }

    captureOpenCollapsibleBlocks() {
      return Array.from(this.querySelectorAll('.main-product__block-collapsible_tab collapsible-tab'))
        .filter((tab) => tab.classList.contains('is-expanded') || tab.getAttribute('open') === 'true')
        .map((tab) => tab.dataset.blockId)
        .filter(Boolean);
    }

    applyOpenCollapsibleBlocks(productInfo, openBlockIds = []) {
      if (!openBlockIds.length) return;

      openBlockIds.forEach((blockId) => {
        const tab = productInfo.querySelector(`.main-product__block-collapsible_tab collapsible-tab[data-block-id="${blockId}"]`);
        if (!tab) return;

        tab.setAttribute('open', 'true');
      });
    }

    syncProductMedia(productInfo) {
      const currentMedia = this.querySelector('.m-main-product--media');
      const nextMedia = productInfo.querySelector('.m-main-product--media');
      if (!currentMedia || !nextMedia) return;

      HTMLUpdateUtility.viewTransition(
        currentMedia,
        nextMedia,
        this.preProcessHtmlCallbacks,
        this.postProcessHtmlCallbacks
      );
    }

    syncStickyAtc(productInfo) {
      const currentStickyAtc = this.querySelector(`#Sticky-Addtocart-${this.sectionId}`);
      const nextStickyAtc = productInfo.querySelector(`#Sticky-Addtocart-${this.sectionId}`);

      if (currentStickyAtc && nextStickyAtc) {
        HTMLUpdateUtility.viewTransition(
          currentStickyAtc,
          nextStickyAtc,
          this.preProcessHtmlCallbacks,
          this.postProcessHtmlCallbacks
        );
      } else if (currentStickyAtc && !nextStickyAtc) {
        currentStickyAtc.remove();
      }
    }

    syncEmbeddedJson(productInfo, selector) {
      const currentScript = this.querySelector(selector);
      const nextScript = productInfo.querySelector(selector);

      if (currentScript && nextScript) {
        currentScript.textContent = nextScript.textContent;
      } else if (!currentScript && nextScript) {
        this.appendChild(nextScript.cloneNode(true));
      } else if (currentScript && !nextScript) {
        currentScript.remove();
      }
    }

    syncVariantFlavorData(html) {
      const flavorScript = Array.from(html.querySelectorAll('script')).find((script) =>
        script.textContent.includes('window.variantFlavorData')
      );
      if (!flavorScript) return;

      const jsonMatch = flavorScript.textContent.match(/window\.variantFlavorData\s*=\s*(\{[\s\S]*\})\s*;?/);
      if (!jsonMatch) return;

      try {
        window.variantFlavorData = JSON.parse(jsonMatch[1]);
      } catch (error) {
        console.error(error);
      }
    }

    syncExternalProductSections(productUrl) {
      const syncableSections = Array.from(document.querySelectorAll('[data-sync-on-product-change="true"]'))
        .filter((section) => !this.contains(section));

      if (!syncableSections.length || !productUrl) return;

      fetch(productUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          syncableSections.forEach((currentSection) => {
            const sectionId = currentSection.dataset.sectionId;
            if (!sectionId) return;

            const nextSection = html.querySelector(
              `[data-sync-on-product-change="true"][data-section-id="${sectionId}"]`
            );

            if (!nextSection) return;

            HTMLUpdateUtility.viewTransition(
              currentSection,
              nextSection,
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          });
        })
        .catch((error) => {
          console.error(error);
        });
    }

    refreshProductState() {
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;
      this.initialMedias = this.productMedia ? this.productMedia.querySelectorAll('.m-product-media--item') : null;
      this.initialThumbs = this.productMedia ? this.productMedia.querySelectorAll(".m-product-media--slider__thumbnails [data-media-type]") : null;

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      } else {
        this.variantGroupImagesData = null;
      }

      this.setQuantityBoundries();

      if (this.currentVariant && !this.showFeaturedMedia) {
        this.updateMedia(this.currentVariant);
      }
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);
      }
    }

    setUnavailable() {
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      const addButton = productForm.querySelector('[name="add"]');

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }
      this.updateAddButtonPrice(productForm, this.currentVariant);

      if (!modifyClass) return;
    }

    updateAddButtonPrice(productForm, variant) {
      const priceWrap = productForm.querySelector('[name="add"] [data-atc-price-wrap]');
      const price = productForm.querySelector('[name="add"] [data-atc-price]');
      if (!priceWrap || !price) return;

      if (!variant) {
        priceWrap.hidden = true;
        return;
      }

      const moneyFormat = window.MinimogSettings?.money_format || "${{amount}}";
      price.textContent = formatMoney(variant.price, moneyFormat);
      priceWrap.hidden = false;
    }

    updateMedia(variant) {
      if (!this.productMedia) return;
      if (this.enableVariantGroupImages && this.variantGroupImagesData.enable) {
        this.handleVariantGroupImage(variant);

        if (this.productMedia.slider) {
          this.productMedia.slider.updateSlides();
          this.productMedia.navSlider.updateSlides();

          this.productMedia.slider.slideTo(0);
          this.productMedia.navSlider.slideTo(0);

          this.productMedia.handleSlideChange();
        }
        if (this.productMedia.enableImageZoom) {
          this.productMedia.handlePhotoswipe();
        }
      } else {
        const setActiveMedia = () => {
          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    handleVariantGroupImage(variant) {
      const selectedVariantData = this.variantGroupImagesData.mapping.find(
        (item) => Number(item.id) === variant.id
      );

      const selectedMedias = (initialItems) => {
        const pinnedMedias = Array.from(initialItems).filter((media) => media.dataset.staticFirst === 'true');
        const reorderableMedias = Array.from(initialItems).filter((media) => media.dataset.staticFirst !== 'true');

        const selectedVariantMedias = reorderableMedias
          .map((media) => {
            const index = selectedVariantData.media.indexOf(media.dataset.mediaId);
            const mediaType = media.dataset.mediaType;
            if (index !== -1 || mediaType !== 'image') {
              return media;
            }
            return null;
          })
          .filter(Boolean);

        if (selectedVariantMedias.length <= 0) {
          return Array.from([...pinnedMedias, ...reorderableMedias]).map((media, index) => {
            media.dataset.index = index;

            media.removeAttribute('style'); // Avoid style of swipe slide item
            media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

            return media;
          });
        }

        const orderedMedias = Object.values(selectedVariantData.media)
          .map(id => selectedVariantMedias.find(media => media.dataset.mediaId === id))
          .filter(Boolean);

        const remainingMedias = selectedVariantMedias.filter(media => !Object.values(selectedVariantData.media).includes(media.dataset.mediaId));

        return Array.from([...pinnedMedias, ...orderedMedias, ...remainingMedias]).map((media, index) => {
          media.dataset.index = index;

          media.removeAttribute('style'); // Avoid style of swipe slide item
          media.querySelector('.m-product-media').setAttribute("style", `--animation-order: ${index + 1};`);

          return media;
        });
      }

      const mediaWrapper = this.productMedia.querySelector(".m-media-gallery__list");
      const thumbWrapper = this.productMedia.querySelector(".m-product-media--slider__thumbnails .swiper-wrapper");

      const layout = this.productMedia.dataset.layout;

      mediaWrapper.innerHTML = "";
      thumbWrapper.innerHTML = "";

      selectedMedias(this.initialMedias).forEach((media, index) => {
        if (layout === "layout-2") {
          media.classList.remove("m-col-span-2");
          if (index % 3 === 0) {
            media.classList.add("m-col-span-2");
          }
        }
        mediaWrapper.append(media);
      });
      selectedMedias(this.initialThumbs).forEach((media) => {
        media.classList.toggle("swiper-slide-thumb-active", media.dataset.index === "0");
        thumbWrapper.append(media);
      });
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${currentVariantId}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}
